/**
 * O que o card não cabe: a ficha da oportunidade, ao clicar nele.
 *
 * ── Por que um lugar para LER, separado do lugar de mexer ────────────────
 *
 * O card mostra cinco coisas porque precisa caber numa coluna ao lado de outras
 * quatro. Previsão de fechamento, dono, há quanto tempo está na etapa e —
 * principalmente — o que foi combinado ficavam de fora, e a única porta para
 * vê-los era o diálogo de EDITAR: quem só queria conferir precisava abrir um
 * formulário, e sair dele sem saber se mexeu em algo sem querer.
 *
 * Aqui se lê e se anota. Corrigir os dados continua sendo do "Editar", que
 * abre a partir daqui.
 *
 * ── A nota se escreve AQUI, e se corrige lá ──────────────────────────────
 *
 * Escrever é parte de acompanhar a negociação — acontece depois da ligação,
 * com o card aberto na frente. Corrigir é mexer num dado já gravado, que é o
 * assunto do outro diálogo. Oferecer as duas coisas nos dois lugares faria a
 * tabela ter dois comportamentos conforme de onde se chegou.
 *
 * ── Um card grande, com abas (24/09/2026) ────────────────────────────────
 *
 * Pedido: ao clicar, abrir um card maior com sub-abas, cada uma com um
 * assunto. Resumo, Notas e Histórico entram primeiro; Conversas e
 * Documentos vêm nas próximas entregas. Sem id canônico só o Resumo existe:
 * as outras abas são endereçadas por ele. O Histórico mostra, desde
 * 23/10/2026, tudo o que acontece dentro da oportunidade, e não só as etapas.
 *
 * ── Painel à direita, metade da tela (09/10/2026) ────────────────────────
 *
 * Pedido: em vez da caixa centrada, a ficha abre encostada à direita,
 * ocupando metade da tela, com o funil desfocado atrás. Com a largura nova:
 *
 *   · etapa, valor e risco viram selos logo abaixo do título, e as ações
 *     (abrir conversa, ver cliente, editar) sobem para o topo — sem rodapé;
 *   · as abas são as de texto sublinhado, e só o corpo rola: título, ações e
 *     abas ficam no lugar enquanto a lista desce;
 *   · o Resumo ganha o andamento no funil, o responsável e a última nota,
 *     que antes exigiam abrir outra aba ou o card.
 */

import { useState, type ReactNode } from 'react';
import { usePode, useSessao } from '@/shared/sessao/contexto';
import { AbaDaConsulta } from './aba-da-consulta';
import type { CardDoFunil } from '@cliqcrm/contracts';
import { useCamposPersonalizados, useNotasDaOportunidade } from '@/shared/api/consultas';
import { Botao, Chip, Dialogo, Segmentado } from '@/shared/ui';
import { HistoricoDoFunil } from './historico-do-funil';
import { ResumoDoAtendimento } from './resumo-do-atendimento';
import { ConversasDaOportunidade } from './conversas-da-oportunidade';
import { DocumentosDaOportunidade } from './documentos-da-oportunidade';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import { NotasDaOportunidade } from '@/features/comercial/notas-da-oportunidade';
import { ValoresDosCampos } from '@/entities/campos/campo-personalizado';

type Aba = 'resumo' | 'consulta' | 'notas' | 'conversas' | 'documentos' | 'historico';

const MOTIVO_DO_RISCO: Record<string, string> = {
  sem_interacao: 'sem interação há dias',
  etapa_parada: 'parada nesta etapa',
};

/** Em reais, com o símbolo. `null` some da ficha em vez de virar "R$ 0,00". */
function dinheiro(valor: CardDoFunil['valor']): string | null {
  if (!valor) return null;
  return (valor.quantia_micros / 1_000_000).toLocaleString('pt-BR', {
    style: 'currency',
    currency: valor.moeda,
  });
}

/** Dia, sem hora: previsão de fechamento é um dia. */
function emDia(instante: string | null): string | null {
  if (!instante) return null;
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleDateString('pt-BR');
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }): ReactNode {
  return (
    <div className="ficha-op__linha">
      <dt className="ficha-op__rotulo">{rotulo}</dt>
      <dd className="ficha-op__valor">{children}</dd>
    </div>
  );
}

/** Data e hora curtas, como na tabela de notas. */
function quandoCurto(instante: string): string {
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Onde a oportunidade está no funil, etapa por etapa.
 *
 * Lista ordenada com `aria-current="step"`, e não só barras coloridas: o
 * leitor de tela anuncia "etapa atual" na certa, e quem não distingue a cor
 * lê o nome em negrito.
 */
function Andamento({
  etapas,
  atual,
}: {
  etapas: readonly { readonly chave: string; readonly nome: string }[];
  atual: string;
}): ReactNode {
  const indice = etapas.findIndex((e) => e.chave === atual);
  // Etapa fora da lista (ganha, perdida, funil trocado): sem trilho a desenhar.
  if (indice < 0) return null;
  return (
    <div className="ficha-op__andamento">
      <div className="ficha-op__andamento-cab">
        <span className="ficha-op__versal">Andamento no funil</span>
        <span>
          {indice + 1} de {etapas.length}
        </span>
      </div>
      <ol className="ficha-op__trilho" aria-label="Andamento no funil">
        {etapas.map((e, i) => (
          <li
            key={e.chave}
            aria-current={i === indice ? 'step' : undefined}
            className={
              i < indice
                ? 'ficha-op__passo ficha-op__passo--feito'
                : i === indice
                  ? 'ficha-op__passo ficha-op__passo--atual'
                  : 'ficha-op__passo'
            }
          >
            {e.nome}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * A nota mais recente, no Resumo.
 *
 * É o que se procura ao abrir a ficha — "o que ficou combinado?" — e antes
 * pedia um clique a mais. A consulta é a mesma da aba Notas, então trocar de
 * aba não busca de novo.
 */
function UltimaNota({
  oportunidadeId,
  aoVerTodas,
}: {
  oportunidadeId: string;
  aoVerTodas: () => void;
}): ReactNode {
  const consulta = useNotasDaOportunidade(oportunidadeId);
  const itens = consulta.data?.itens ?? [];
  const nota = itens[0];

  return (
    <section className="ficha-op__bloco" aria-label="Última nota">
      <div className="ficha-op__bloco-cab">
        <h3>Última nota</h3>
        {itens.length > 0 && (
          <button type="button" className="ficha-op__link" onClick={aoVerTodas}>
            Ver todas ({itens.length})
          </button>
        )}
      </div>
      {consulta.isPending ? (
        <p className="notas__vazio">Buscando o que já foi anotado…</p>
      ) : nota ? (
        <div className="ficha-op__nota">
          <span className="ficha-op__nota-meta">
            <strong>{nota.autor?.nome ?? (nota.autor ? 'sem nome' : 'autor removido')}</strong>
            {' · '}
            {quandoCurto(nota.criada_em)}
            {nota.editada_em && <span className="notas__editada">editada</span>}
          </span>
          <p className="ficha-op__nota-texto">{nota.texto}</p>
        </div>
      ) : (
        <p className="notas__vazio">
          Nada anotado ainda. Escreva o que foi combinado, como prazo, condição ou objeção, na aba
          Notas.
        </p>
      )}
    </section>
  );
}

export function DialogoDaOportunidade({
  card,
  etapas,
  anotando,
  erroDaNota,
  aoAnotar,
  aoEditar,
  aoAbrirConversa,
  aoAbrirCliente,
  aoFechar,
}: {
  card: CardDoFunil;
  /** Para mostrar o NOME da etapa, e não a chave que o CRM guarda. Em ordem. */
  etapas: readonly { readonly chave: string; readonly nome: string }[];
  anotando: boolean;
  erroDaNota?: string | null;
  aoAnotar: (texto: string) => void;
  /**
   * Ausente — como no Cliente 360 —, o botão "Editar dados" não aparece: a
   * edição mora no funil, com a regra de etapa e de perda.
   */
  aoEditar?: () => void;
  aoAbrirConversa: (conversaId: string) => void;
  /**
   * Leva ao Cliente 360 de quem é o negócio (28/09/2026). Ausente — como no
   * próprio 360, que já é a ficha do cliente —, o botão não aparece.
   */
  aoAbrirCliente?: (clienteId: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const valor = dinheiro(card.valor);
  const previsao = emDia(card.fechamento_previsto_em);
  const etapa = etapas.find((e) => e.chave === card.etapa)?.nome ?? card.etapa;
  const [aba, definirAba] = useState<Aba>('resumo');
  const camposDaOportunidade = useCamposPersonalizados('oportunidade');
  const { estado } = useSessao();
  const editaOportunidade = usePode('oportunidade', 'editar');
  /** A clínica (#811): a aba "Consulta" só existe com a funcionalidade ligada. */
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);
  const risco = card.risco.sinais.map((sinal) => MOTIVO_DO_RISCO[sinal] ?? sinal).join(' · ');

  return (
    <Dialogo titulo={card.titulo ?? 'Oportunidade sem título'} forma="lateral" aoFechar={aoFechar}>
      <div className="ficha-op__topo">
        <div className="ficha-op__selos">
          <span className="ficha-op__etapa">
            {etapa}
            {card.etapa_desde_em && (
              <span className="ficha-op__desde"> · há {idadeEmPalavras(card.etapa_desde_em)}</span>
            )}
          </span>
          {valor && <span className="ficha-op__dinheiro">{valor}</span>}
          {card.risco.em_risco && <Chip tom="atencao">em risco</Chip>}
        </div>

        <div className="ficha-op__acoes">
          {card.conversa_id && (
            <Botao onClick={() => aoAbrirConversa(card.conversa_id!)}>Abrir conversa</Botao>
          )}
          {/*
            Só com cliente identificado: sem o id canônico não há ficha a abrir,
            e um botão que leva a lugar nenhum é pior que botão nenhum.
          */}
          {aoAbrirCliente && card.cliente?.id && (
            <Botao onClick={() => aoAbrirCliente(card.cliente!.id!)}>Ver cliente</Botao>
          )}
          {aoEditar && (
            <span className="ficha-op__acao-principal">
              <Botao
                variante="primario"
                onClick={aoEditar}
                /*
                  Sem canônico não há o que editar: o card veio da engine e ainda
                  não foi cunhado. É a mesma trava que já vale para mover.
                */
                desabilitado={card.id === null}
                motivo="Criada fora do CliqCRM: não é possível editar por aqui."
              >
                Editar dados
              </Botao>
            </span>
          )}
        </div>
      </div>

      {card.id && (
        <div className="ficha-op__abas">
          <Segmentado<Aba>
            rotulo="Partes da oportunidade"
            aparencia="sublinhado"
            opcoes={[
              { valor: 'resumo', rotulo: 'Resumo' },
              ...(clinica ? [{ valor: 'consulta' as const, rotulo: 'Consulta' }] : []),
              { valor: 'notas', rotulo: 'Notas' },
              { valor: 'conversas', rotulo: 'Conversas' },
              { valor: 'documentos', rotulo: 'Documentos' },
              { valor: 'historico', rotulo: 'Histórico' },
            ]}
            escolhida={aba}
            aoEscolher={definirAba}
          />
        </div>
      )}

      <div className="ficha-op__corpo">
        {aba === 'resumo' && (
          <div role="tabpanel" aria-label="Resumo" className="ficha-op__painel">
            <Andamento etapas={etapas} atual={card.etapa} />

            <dl className="ficha-op">
              {valor && <Linha rotulo="Valor">{valor}</Linha>}
              <Linha rotulo="Etapa">
                {etapa}
                {card.etapa_desde_em && (
                  <span className="ficha-op__desde">
                    {' '}
                    · há {idadeEmPalavras(card.etapa_desde_em)}
                  </span>
                )}
              </Linha>
              {card.cliente?.nome_exibicao && (
                <Linha rotulo="Cliente">{card.cliente.nome_exibicao}</Linha>
              )}
              {card.responsavel?.nome && (
                <Linha rotulo="Responsável">{card.responsavel.nome}</Linha>
              )}
              {previsao && <Linha rotulo="Previsão de fechamento">{previsao}</Linha>}
              {card.ultima_interacao_em && (
                <Linha rotulo="Última interação">
                  há {idadeEmPalavras(card.ultima_interacao_em)}
                </Linha>
              )}
              {card.risco.em_risco && (
                /*
                  O risco aparece com o MOTIVO, como no card: "em risco" sozinho
                  obriga a procurar qual dos dois é, e a ficha existe para não
                  mandar ninguém procurar.
                */
                <Linha rotulo="Risco">
                  <span className="ficha-op__risco">{risco}</span>
                </Linha>
              )}
            </dl>

            {/* O que as rotinas trouxeram do SISAC para este card (#1151), com o total. */}
            {clinica && card.id && <ResumoDoAtendimento oportunidadeId={card.id} />}

            {/* Os campos personalizados da oportunidade (#568), pela cópia do funil. */}
            <ValoresDosCampos
              campos={camposDaOportunidade.data?.itens ?? []}
              valores={card.campos}
            />

            {card.id ? (
              <UltimaNota oportunidadeId={card.id} aoVerTodas={() => definirAba('notas')} />
            ) : (
              /*
                Sem canônico não há onde pendurar nota nem histórico — as abas são
                endereçadas pelo id que a ingestão cunha. Dizer isso é melhor que
                mostrar abas que devolveriam erro ao primeiro clique.
              */
              <p className="notas__vazio">
                Notas e histórico ficam disponíveis assim que esta oportunidade terminar de
                sincronizar.
              </p>
            )}
          </div>
        )}

        {aba === 'consulta' && clinica && card.id && (
          <div role="tabpanel" aria-label="Consulta" className="ficha-op__painel">
            <AbaDaConsulta oportunidadeId={card.id} podeEditar={editaOportunidade} />
          </div>
        )}

        {aba === 'notas' && card.id && (
          <div role="tabpanel" aria-label="Notas" className="ficha-op__painel">
            <NotasDaOportunidade
              oportunidadeId={card.id}
              aoEscrever={aoAnotar}
              salvando={anotando}
              erro={erroDaNota}
            />
          </div>
        )}

        {aba === 'conversas' && card.id && (
          <div role="tabpanel" aria-label="Conversas" className="ficha-op__painel">
            <ConversasDaOportunidade oportunidadeId={card.id} aoAbrirConversa={aoAbrirConversa} />
          </div>
        )}

        {aba === 'documentos' && card.id && (
          <div role="tabpanel" aria-label="Documentos" className="ficha-op__painel">
            <DocumentosDaOportunidade oportunidadeId={card.id} />
          </div>
        )}

        {aba === 'historico' && card.id && (
          <div role="tabpanel" aria-label="Histórico" className="ficha-op__painel">
            <HistoricoDoFunil oportunidadeId={card.id} clinica={clinica} />
          </div>
        )}
      </div>
    </Dialogo>
  );
}
